import React, { useState } from 'react';

import { keys } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  useEligibleApprovalReviewers,
  useRequestApprovals,
} from 'hooks/useApprovals';
import { useGetPositionsForIncidentId } from 'hooks/incident/useRoster';
import { useUserItemId } from 'components/IAP/hooks/commonHooks';
import { ITEM_TYPES } from 'constants/itemType';
import { toast } from 'react-toastify';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import RequestApprovalConfirmationModal from 'components/Reports/RequestApprovalConfirmationModal';
import RequestApprovalSuccessModal from 'components/Reports/RequestApprovalSuccessModal';

const POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL = [
  'Situation Unit Leader',
  'Planning Section Chief',
  'Incident Commander',
];

type RequestApprovalButtonProps = {
  sitrep: GetSitrepsResponse;
  isEditMode?: boolean;
};

const RequestApprovalButton: React.FC<RequestApprovalButtonProps> = ({
  sitrep,
  isEditMode = false,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [pendingSuccess, setPendingSuccess] = useState(false);
  const userId = useUserItemId();
  const requestApprovals = useRequestApprovals(keys.base, {
    suppressSuccessToast: true,
    deferQueryInvalidation: true,
  });
  const aorEligibleReviewersQuery = useEligibleApprovalReviewers({
    itemId: sitrep.item_id,
    enabled: !sitrep.incident_id,
  });
  const incidentPositionsQuery = useGetPositionsForIncidentId(
    sitrep.incident_id || ''
  );
  const filteredAorReviewers =
    aorEligibleReviewersQuery.data?.filter(
      (r) => r.reviewerItemId !== userId
    ) ?? [];

  const handleConfirmApproval = async () => {
    let reviewers: { reviewerItemId: string }[];

    if (sitrep.incident_id) {
      const positions = incidentPositionsQuery.data;
      reviewers = POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL.map(
        (positionName) => {
          const position = positions?.find((p) => p.name === positionName);
          if (!position) {
            return null;
          }
          return { reviewerItemId: position.item_id };
        }
      ).filter((r): r is { reviewerItemId: string } => r !== null);
    } else {
      reviewers = filteredAorReviewers.map((r) => ({
        reviewerItemId: r.reviewerItemId,
      }));

      if (reviewers.length === 0) {
        toast.info('No eligible reviewers found. Request sent to you only.');
        setPendingSuccess(true);
        setShowConfirmation(false);
        return;
      }
    }

    await requestApprovals.mutateAsync({
      itemId: sitrep.item_id,
      itemType: ITEM_TYPES.Sitrep,
      reviewers,
      approvalsRequiredInOrder: !!sitrep.incident_id,
      groupId: sitrep.group_guid,
      incidentId: sitrep.incident_id || undefined,
    });
    setPendingSuccess(true);
    setShowConfirmation(false);
  };

  return (
    <>
      <StylishNewButton
        className="button--primary"
        type="button"
        disabled={requestApprovals.isPending || isEditMode}
        title={
          isEditMode ? 'Finish editing before requesting approval' : undefined
        }
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setShowConfirmation(true);
        }}
        style={{
          /**
           * Why is this here!? It's because RequestApprovalButton is rendered
           * inside a CollapsibleCard. All disabled buttons have pointerEvents:
           * 'none'. Normally that wouldn't be a problem, but in a nested
           * clickable context, we actually want the pointer event to bubble up
           * so it can hit the event.stopPropogation() and prevent the card for
           * collapsing.
           */
          pointerEvents: 'auto',
        }}
      >
        Request Approval
      </StylishNewButton>
      <RequestApprovalConfirmationModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        onConfirm={handleConfirmApproval}
        isSubmitting={requestApprovals.isPending}
        name={sitrep.name}
        createdBy={sitrep.created_by_user_name ?? 'Unknown'}
        createdAt={sitrep.timestamp}
        editedBy={
          sitrep.updated_by_user_name ??
          sitrep.created_by_user_name ??
          'Unknown'
        }
        editedAt={sitrep.updated_at ?? sitrep.timestamp}
        incidentId={sitrep.incident_id}
        aorReviewers={filteredAorReviewers}
        positionNames={
          sitrep.incident_id
            ? POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL
            : filteredAorReviewers.map((r) => r.displayName)
        }
        fallbackReviewerLabel={
          sitrep.incident_id
            ? undefined
            : 'No SITREP reviewers with AOR permissions are configured for this report (excluding you).'
        }
        onExited={() => {
          if (pendingSuccess) {
            setPendingSuccess(false);
            setShowSuccess(true);
          }
        }}
      />
      <RequestApprovalSuccessModal
        show={showSuccess}
        draftName={sitrep.name}
        editedBy={
          sitrep.updated_by_user_name ??
          sitrep.created_by_user_name ??
          'Unknown'
        }
        incidentId={sitrep.incident_id}
        onHide={() => {
          setShowSuccess(false);
          requestApprovals.invalidateQueries();
        }}
      />
    </>
  );
};

export default RequestApprovalButton;
